/**
 * Labels, defaults and input config for the clearance ladder planner.
 *
 * English-only by design: the tool lives under /tools, which ships to the
 * English locales only (see EN_ONLY_SLUGS). Currency is the one locale-varying
 * piece and is resolved at render time from the request locale, not hardcoded
 * here.
 */

import type { PriceSensitivity } from "@/lib/bf-toolkit/calculations";
import { PRICE_SENSITIVITY_ELASTICITY } from "@/lib/bf-toolkit/calculations";

export const STRINGS = {
  toolTitle: "Your numbers",
  strategyTitle: "Markdown strategy",
  // Left-column input labels: terse, single-line, no inline hints, so the
  // input boxes line up cleanly in a grid.
  unitsRemaining: "Units remaining",
  sellingPrice: "Selling price",
  unitCost: "Unit cost",
  targetDate: "Clear-out date",
  sellThrough: "Sell-through / wk",
  sellThroughHint: "Units you sell a week at full price.",
  holdingCost: "Storage / unit / wk",
  holdingCostHint: "Cost to hold one unit for a week.",
  holdingCostPlaceholder: "0.10 (optional)",
  sensitivityLabel: "How price-sensitive are your customers?",
  sensitivityHint: "Sets how much a markdown lifts weekly sales.",
  advancedToggle: "Fine-tune",
  elasticityLabel: "Price sensitivity",
  tiersLabel: "Discount steps",
  tiersHint: "Each step only kicks in if you're behind pace to clear in time.",
  startTier: "Starting discount",
  midTier: "Step up to",
  finalTier: "Final push",
  notNeededTag: "not needed",
  resultPrompt:
    "Enter your units, price, cost, sell-through and clear-out date to see the plan.",
  // Edge-case states
  pastTargetHeadline: "That date has already arrived.",
  pastTargetBody:
    "Pick a clear-out date at least a week out to get a markdown schedule.",
  alreadyClearedHeadline: "Nothing left to clear.",
  alreadyClearedBody: "You're already sold through, so no markdown needed.",
  chartBaseline: "Do nothing",
  chartLadder: "With markdown",
  clearedTag: "cleared",
  phaseNowLabel: "Now",
  perWeek: "/wk",
  units: "units",
  // Compact schedule strip
  scheduleTitle: "Your markdown schedule",
  phaseNames: {
    start: "Now",
    mid: "Step up",
    final: "Final push",
  },
  // Worth-it comparison
  compareTitle: "Is it worth it?",
  compareLadder: "Markdown",
  compareNothing: "Ride it out",
  cashHeroLabel: "Cash in pocket by",
  rowStockCleared: "Stock sold",
  rowStockLeft: "Left (at cost)",
  rowMargin: "Gross profit on sales",
  rowDiscountCost: "Discount cost",
  rowStorage: "Storage",
  // No-discount edge case
  noDiscountHeadline: "With no discount, this is just riding it out.",
  disclaimer:
    "Estimates only, based on the numbers you enter. Sell-through at each discount is modelled from the price-sensitivity assumption you set, using a standard price-elasticity curve; it is not a forecast or based on Wayflyer data. Real demand depends on your product, audience and timing.",
} as const;

export const PERCENT_RANGE = { min: 0, max: 100, step: 1 };
export const ELASTICITY_SLIDER = { min: 0, max: 4, step: 0.1 };

/** Price-sensitivity chips, in ascending order of responsiveness. Each maps to
 * an elasticity in {@link PRICE_SENSITIVITY_ELASTICITY}. */
export const SENSITIVITY_OPTIONS: {
  value: PriceSensitivity;
  label: string;
  elasticity: number;
}[] = [
  {
    value: "cautious",
    label: "Cautious buyers",
    elasticity: PRICE_SENSITIVITY_ELASTICITY.cautious,
  },
  {
    value: "average",
    label: "Average",
    elasticity: PRICE_SENSITIVITY_ELASTICITY.average,
  },
  {
    value: "deal-driven",
    label: "Deal-driven",
    elasticity: PRICE_SENSITIVITY_ELASTICITY["deal-driven"],
  },
];

/**
 * Neutral, round placeholder values the user is meant to change. Deliberately
 * not derived from any Wayflyer merchant data. At 10 units/week the current
 * pace clears only a fraction of 500 units before the year-end target, so the
 * default view shows the ladder doing real work rather than a trivial
 * already-clears-at-full-price case.
 */
export const DEFAULTS = {
  unitsRemaining: "500",
  sellingPrice: "25",
  unitCost: "15",
  sellThrough: "10",
  sensitivity: "average" as PriceSensitivity,
  tiers: { start: 20, mid: 30, final: 50 },
  holdingCost: "",
} as const;

export interface FaqQuestion {
  question: string;
  answer: string;
}

export const FAQ_TITLE = "Clearance ladder FAQ";

export const FAQ_QUESTIONS: FaqQuestion[] = [
  {
    question: "What is a clearance ladder?",
    answer:
      "A clearance ladder is a markdown schedule that steps the discount up in stages rather than cutting to one price on day one. This tool builds you a three-step ladder (a starting discount, a mid-point step-up and a final push) that only steps up if you're falling behind the pace needed to clear your stock by your target date.",
  },
  {
    question: "What is sell-through rate?",
    answer:
      "Sell-through rate is how much of your stock you sell over a given period, usually shown as units sold per week or as a percentage of what you started with. This tool uses your current weekly sell-through at full price as the baseline it measures every markdown against, so it can tell whether a discount is actually needed to hit your clear-out date.",
  },
  {
    question:
      "What is price elasticity of demand, and how does this tool use it?",
    answer:
      "Price elasticity of demand measures how much a change in price changes how much people buy: highly elastic demand means a small discount drives a big jump in sales, while inelastic demand means price barely moves the needle. This tool turns that into a plain **How price-sensitive are your customers?** choice (cautious, average or deal-driven) and uses it to model how each markdown tier lifts your weekly sell-through, with a **Fine-tune** option to set the underlying elasticity value directly.",
  },
  {
    question: "How does the tool decide when to step up the discount?",
    answer:
      "Each step in the ladder only kicks in once you fall behind the pace needed to clear your units by the target date at the current discount. If your starting discount is already clearing stock in time, the tool never steps up to the deeper tiers, so you're not discounting further than you need to.",
  },
  {
    question: "Is marking down always worth it, or should I hold my price?",
    answer:
      "Not always. The **Is it worth it?** comparison weighs the markdown schedule against simply holding your price and riding out slower sales, accounting for the gross profit on what actually sells, the cost of the discount itself, and any storage cost on units left over. If holding wins, the tool will show that too.",
  },
  {
    question: "Does this account for storage costs on unsold stock?",
    answer:
      "Yes, if you enter one. The optional **storage / unit / week** field adds a holding cost for every week stock sits unsold, which is factored into the ride-it-out comparison, since stock quietly costs money the longer it sits.",
  },
  {
    question: "Where can I get help with the rest of my Black Friday plan?",
    answer:
      "Check your margin on the original discount with the [discount break-even calculator](/tools/discount-break-even), or see how you'd grade a whole Black Friday weekend with the [Black Friday report card](/tools/bf-report-card). Browse every tool in the [full Black Friday toolkit](/tools).",
  },
];
